import { z } from "zod";
import {
  MODULE_IDS,
  WEAPON_IDS,
  type ModuleId,
  type WeaponId,
} from "./definitions.js";
import { PASSIVE_IDS, type PassiveId } from "./contracts.js";
import type { EquipmentTiers } from "./equipment-tiers.js";
import {
  HULL_MODELS,
  hullModelIdSchema,
  type HullModelId,
} from "./hull-models.js";
import { SHIP_SYSTEM_IDS } from "./ship-systems.js";

export const MAX_SHIP_CELLS = 11;
export const fittingTierSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
]);
export const weaponMountSchema = z.strictObject({
  id: z.enum(WEAPON_IDS),
  tier: fittingTierSchema,
});
export const utilityMountSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("module"),
    id: z.enum(MODULE_IDS),
    tier: fittingTierSchema,
  }),
  z.strictObject({
    kind: z.literal("passive"),
    id: z.enum(PASSIVE_IDS).exclude(["none"]),
    tier: fittingTierSchema,
  }),
  z.strictObject({
    kind: z.literal("system"),
    id: z.enum(SHIP_SYSTEM_IDS),
    tier: fittingTierSchema,
  }),
]);
export const fittingItemSchema = z.discriminatedUnion("kind", [
  weaponMountSchema.extend({ kind: z.literal("weapon") }),
  ...utilityMountSchema.options,
]);
export type FittingItem = z.infer<typeof fittingItemSchema>;
export type UtilityMount = z.infer<typeof utilityMountSchema>;
export type WeaponMount = z.infer<typeof weaponMountSchema>;

/** Historical split mounts remain readable without changing a frozen flight. */
export const legacyShipFittingSchema = z.strictObject({
  format: z.literal(1),
  hullId: hullModelIdSchema,
  weapons: z.array(weaponMountSchema.nullable()).max(3),
  utilities: z.array(utilityMountSchema.nullable()).max(8),
});
export type LegacyShipFitting = z.infer<typeof legacyShipFittingSchema>;
export const shipFittingSchema = z.strictObject({
  format: z.literal(2),
  hullId: hullModelIdSchema,
  cells: z.array(fittingItemSchema.nullable()).max(MAX_SHIP_CELLS),
});
export type ShipFitting = z.infer<typeof shipFittingSchema>;
export const storedShipFittingSchema = z.discriminatedUnion("format", [
  legacyShipFittingSchema,
  shipFittingSchema,
]);
export type StoredShipFitting = z.infer<typeof storedShipFittingSchema>;
/** Current installation constraints are independent from historical snapshot decoding. */
export const currentShipFittingSchema = shipFittingSchema.superRefine(
  (fitting, context) => {
    if (fitting.cells.length !== HULL_MODELS[fitting.hullId].equipmentSlots)
      context.addIssue({
        code: "custom",
        path: ["cells"],
        message: "Equipment cells must match the ship",
      });
  },
);
export const fittingSlotSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("cell"),
    index: z
      .number()
      .int()
      .min(0)
      .max(MAX_SHIP_CELLS - 1),
  }),
  z.strictObject({
    kind: z.literal("weapon"),
    index: z.number().int().min(0).max(2),
  }),
  z.strictObject({
    kind: z.literal("utility"),
    index: z.number().int().min(0).max(7),
  }),
]);
export type FittingSlot = z.infer<typeof fittingSlotSchema>;
export const fittingUpgradeTargetSchema = z.strictObject({
  hullId: hullModelIdSchema,
  slot: fittingSlotSchema,
});
export type FittingUpgradeTarget = z.infer<typeof fittingUpgradeTargetSchema>;
// Historical request bodies are accepted only to replay their already committed receipts.
export const shipFittingInstallSchema = z.strictObject({
  requestId: z.string().min(8).max(100),
  fitting: storedShipFittingSchema,
});
export type ShipFittingInstall = z.infer<typeof shipFittingInstallSchema>;

export function emptyShipFitting(hullId: HullModelId): ShipFitting {
  return {
    format: 2,
    hullId,
    cells: Array.from(
      { length: HULL_MODELS[hullId].equipmentSlots },
      () => null,
    ),
  };
}
export function fittingCells(
  fitting: StoredShipFitting,
): (FittingItem | null)[] {
  return fitting.format === 2
    ? fitting.cells
    : [
        ...fitting.weapons.map((item) =>
          item ? { kind: "weapon" as const, ...item } : null,
        ),
        ...fitting.utilities,
      ];
}
export function currentShipFitting(fitting: StoredShipFitting): ShipFitting {
  const cells = structuredClone(fittingCells(fitting));
  const size = HULL_MODELS[fitting.hullId].equipmentSlots;
  while (cells.length > size && cells.at(-1) === null) cells.pop();
  while (cells.length < size) cells.push(null);
  return { format: 2, hullId: fitting.hullId, cells };
}
export function fittingWeapons(fitting: StoredShipFitting) {
  if (fitting.format === 1)
    return fitting.weapons.flatMap((item, slot) =>
      item ? [{ item, slot }] : [],
    );
  return fitting.cells.flatMap((item, slot) =>
    item?.kind === "weapon" ? [{ item, slot }] : [],
  );
}
export function fittingModules(fitting: StoredShipFitting) {
  const cells = fitting.format === 1 ? fitting.utilities : fitting.cells;
  return cells.flatMap((item, slot) =>
    item?.kind === "module" ? [{ item, slot }] : [],
  );
}
export function fittingSlotSpans(fitting: StoredShipFitting) {
  return fitting.format === 1
    ? { weapons: fitting.weapons.length, abilities: fitting.utilities.length }
    : { weapons: fitting.cells.length, abilities: fitting.cells.length };
}

export interface LegacyLoadout {
  hullId: HullModelId;
  weaponId: WeaponId;
  moduleId: ModuleId;
  secondaryModuleId: ModuleId;
  passiveId: PassiveId;
  equipmentTiers: EquipmentTiers;
}
/** Only a new selection/launch uses this bridge; old flights keep their original fields/rules. */
export function legacyShipFitting(loadout: LegacyLoadout): ShipFitting {
  const fitting = emptyShipFitting(loadout.hullId);
  fitting.cells[0] = {
    kind: "weapon",
    id: loadout.weaponId,
    tier: loadout.equipmentTiers.weapon,
  };
  fitting.cells[1] = {
    kind: "module",
    id: loadout.moduleId,
    tier: loadout.equipmentTiers.module,
  };
  fitting.cells[2] = {
    kind: "module",
    id: loadout.secondaryModuleId,
    tier: loadout.equipmentTiers.secondaryModule,
  };
  fitting.cells[3] =
    loadout.passiveId === "none"
      ? { kind: "system", id: "shield", tier: 0 }
      : {
          kind: "passive",
          id: loadout.passiveId,
          tier: loadout.equipmentTiers.passive,
        };
  return fitting;
}
export function legacyHardpointFitting(
  loadout: LegacyLoadout,
  weaponSlots: number,
  equipmentSlots: number,
): LegacyShipFitting {
  const source = legacyShipFitting(loadout);
  return {
    format: 1,
    hullId: loadout.hullId,
    weapons: Array.from({ length: weaponSlots }, (_, index) =>
      index === 0
        ? { id: loadout.weaponId, tier: loadout.equipmentTiers.weapon }
        : null,
    ),
    utilities: Array.from({ length: equipmentSlots }, (_, index) => {
      const item = source.cells[index + 1];
      return item && item.kind !== "weapon" ? item : null;
    }),
  };
}

/** Every occupied cell reserves one owned instance, including repeated weapons. */
export function mountedEquipment(fitting: StoredShipFitting) {
  const totals = new Map<string, { item: FittingItem; count: number }>();
  for (const item of fittingCells(fitting)) {
    if (!item) continue;
    const key = `${item.kind}:${item.id}:${item.tier}`;
    const previous = totals.get(key);
    if (previous) previous.count++;
    else totals.set(key, { item, count: 1 });
  }
  return [...totals.values()];
}
